import { useActionSheet } from "@expo/react-native-action-sheet";
import { MaterialIcons } from "@expo/vector-icons";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import _ from "lodash";
import {
  Box,
  Center,
  CheckIcon,
  ChevronDownIcon,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Pressable,
  ScrollView,
  Select,
  Spinner,
  Text,
  useTheme,
  VStack,
} from "native-base";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Modal, SafeAreaView, TouchableOpacity } from "react-native";
import { useSelector } from "react-redux";
import * as URI from "uri-js";

import { store } from "../../../store";
import { CommonCategoryCard } from "../commons";
import CommonHeaderDivider from "../commons/CommonHeaderDivider";
import RecipeMealCard from "../components/RecipeCard";
import { commonStyles, Images, isDesktopScreen, Routes, Scale, width } from "../constants";
import { MealType, RecipeCategory, RecipeTag, SourceProvider } from "../constants/data";
import { addMealToPlanner, generateMealsPrefetchOptions, getPlannerData } from "../helpers/diaryHelpers";
import { formatMealType } from "../helpers/generalHelpers";
import {
  FeatureFlag,
  getOrganisation,
  isFeatureFlagEnabled,
  shouldHaveAccessToCreateYourOwnRecipes,
} from "../helpers/userHelpers";
import type { RecipeSearchScreenParams, RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  FoodGenerateMealsListApiArg,
  GeneratedRecipeMeal,
  MealTypeEnum,
  RecipeMeal,
  RecipeMealRequest,
  RecipeTemplate,
  RecipeTemplateRequest,
} from "../services/backendTypes";
import { api } from "../services/baseApi";
import logger from "../services/logger";
import { userSelector, viewAsUserSelector } from "../slices/userSlice";
import type { CategoryFilter, RecipeMacrosItemType, RecipeTagOrCategory } from "../types";
import styles from "./RecipeSearchScreenStyle";

// NOTE: There is a scoping problem with usePrefetch, I don't know why
// eslint-disable-next-line @typescript-eslint/unbound-method
const {
  useFoodRecipeMealCreateMutation,
  useFoodGenerateMealsListQuery,
  usePlannerCalendarItemCreateMutation,
  usePlannerPlanGeneratedRecipeMealCreateMutation,
  usePlannerCalendarItemDestroyMutation,
} = backendApi;

const RECIPE_TAG_TO_NAME = {
  [RecipeTag.BREAD]: "Bread",
  [RecipeTag.BURGER]: "Burger",
  [RecipeTag.COUSCOUS_AND_QUINOA]: "Couscous and quinoa",
  [RecipeTag.DAIRY]: "Dairy",
  [RecipeTag.DIPS_AND_SPREADS]: "Dips and spreads",
  [RecipeTag.KETO]: "Keto",
  [RecipeTag.PANCAKES_AND_OMELETTES]: "Pancakes and omelettes",
  [RecipeTag.PASTA]: "Pasta",
  [RecipeTag.POTATO]: "Potato",
  [RecipeTag.RICE_AND_NOODLES]: "Rice and noodles",
  [RecipeTag.SALAD]: "Salad",
  [RecipeTag.SNACKS_AND_BAKES]: "Snacks and cakes",
  [RecipeTag.SOUP]: "Soup",
  [RecipeTag.VEGAN]: "Vegan",
  [RecipeTag.WRAP]: "Wrap",
};

const TAG_CATEGORY_LIST: CategoryFilter[] = (Object.keys(RecipeTag) as Array<keyof typeof RecipeTag>).map((tag, i) => ({
  id: `${i}_tag`,
  label: RECIPE_TAG_TO_NAME[RecipeTag[tag]],
  value: RecipeTag[tag],
  type: "image",
  image: Images.CategoryBrood, // FIXME: use appropriate image
}));

enum SelectedMealPreparationTime {
  LTE_5 = "5 min. or less",
  LTE_10 = "10 min. or less",
  LTE_15 = "15 min. or less",
  LTE_20 = "20 min. or less",
  LTE_30 = "30 min. or less",
  GTE_30 = "30 min. or more",
  ALL = "All preparation times",
}

const SELECTED_MEAL_PREPARATION_TIME_TO_QUERY_ARGS = {
  [SelectedMealPreparationTime.LTE_5]: { preparationTimeMinLte: 5 },
  [SelectedMealPreparationTime.LTE_10]: { preparationTimeMinLte: 10 },
  [SelectedMealPreparationTime.LTE_15]: { preparationTimeMinLte: 15 },
  [SelectedMealPreparationTime.LTE_20]: { preparationTimeMinLte: 20 },
  [SelectedMealPreparationTime.LTE_30]: { preparationTimeMinLte: 30 },
  [SelectedMealPreparationTime.GTE_30]: { preparationTimeMinGte: 30 },
  [SelectedMealPreparationTime.ALL]: undefined,
};

// TODO: Put these in a data file
const sampleMacrosForDisplay: RecipeMacrosItemType[] = [
  {
    macroName: "kcal",
    progress: 630,
    total: 716,
    unit: "kcal",
  },
  {
    macroName: "protein",
    progress: 8,
    total: 18,
    unit: "g",
  },
  {
    macroName: "fat",
    progress: 23,
    total: 27,
    unit: "g",
  },
  {
    macroName: "carbohydrates",
    progress: 16,
    total: 33,
    unit: "g",
  },
];

type ModalContentProps = {
  modalizeRef: React.Ref<Modal>;
};

type RecipeMealSearchByPageInternal = { [number: number]: GeneratedRecipeMeal[] };
type RecipeMealSearchByPage = { byPage: RecipeMealSearchByPageInternal };

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, Routes.RecipeSearchScreen>;
  route: {
    params: RecipeSearchScreenParams;
  };
};

const recipeMealSearchInitialState = { byPage: {} };

const MemoizedRecipeMealCard = React.memo(
  RecipeMealCard,
  (prevProps, nextProps) => prevProps.recipeMeal.recipe_template.id === nextProps.recipeMeal.recipe_template.id
);

const GENERATE_MEALS_ENDPOINT_NAME = "foodGenerateMealsList";

const RecipeSearchScreen = ({
  navigation,
  route: {
    params: { mealSlotSpecification, currentCalendarDay, calendarItemToReplace },
  },
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const CATEGORY_TO_NAME = {
    [RecipeCategory.POPULAR]: "Popular",
    [RecipeCategory.FAVOURITE]: t("recipe_search.category_favourite"),
    [RecipeCategory.OWN_RECIPE]: t("recipe_search.category_own"),
  };

  const POPULAR_CATEGORY = {
    id: `${1}_category`,
    label: CATEGORY_TO_NAME[RecipeCategory.POPULAR],
    value: RecipeCategory.POPULAR,
    image: Images.CategoryMostChosen,
  };
  const FAVOURITE_CATEGORY: CategoryFilter = {
    id: `${2}_category`,
    label: CATEGORY_TO_NAME[RecipeCategory.FAVOURITE],
    value: RecipeCategory.FAVOURITE,
    type: "image",
    image: Images.CategoryFavourite,
  };
  const OWN_MEAL_CATEGORY: CategoryFilter = {
    id: `${3}_category`,
    label: CATEGORY_TO_NAME[RecipeCategory.OWN_RECIPE],
    value: RecipeCategory.OWN_RECIPE,
    type: "image",
    image: Images.CategoryOwnRecipe,
  };

  const CATEGORY_LIST = [POPULAR_CATEGORY, FAVOURITE_CATEGORY, OWN_MEAL_CATEGORY];

  // NOTE: We are not showing these filters because they are out of scope for the current release
  const CATEGORY_FILTERS = [...CATEGORY_LIST, ...TAG_CATEGORY_LIST];

  const theme = useTheme();

  const MEAL_TYPE_LIST = (Object.keys(MealType) as Array<keyof typeof MealType>).map((mealType, i) => ({
    id: i,
    label: formatMealType(MealType[mealType as MealTypeEnum], t),
    value: MealType[mealType],
  }));

  const viewAsUser = useSelector(viewAsUserSelector);
  const realUser = useSelector(userSelector);
  const user = viewAsUser || realUser;
  if (!user) {
    throw new Error("User is not set");
  }

  const organisation = getOrganisation(user);
  const productLoggingIsDisabled = isFeatureFlagEnabled(organisation || null, FeatureFlag.DisableProductLogging);
  const recipeTemplateEditable = !productLoggingIsDisabled;
  const ownMealsAvailable = !productLoggingIsDisabled || shouldHaveAccessToCreateYourOwnRecipes(user);

  const CREATE_RECIPE_CATEGORY: CategoryFilter = {
    id: "create_recipe",
    label: t("planner.create_your_own_recipe_button_text"),
    value: "CREATE_RECIPE",
    type: "emoji",
    emoji: "👩‍🍳",
  };

  const FILTERS_TO_SHOW = ownMealsAvailable
    ? [CREATE_RECIPE_CATEGORY, FAVOURITE_CATEGORY, OWN_MEAL_CATEGORY]
    : [FAVOURITE_CATEGORY];

  const [page, setPage] = useState<number>(1);
  const [recipeMealsByPage, setRecipeMealsByPage] = useState<RecipeMealSearchByPage>(recipeMealSearchInitialState);
  const [searchText, setSearchText] = useState("");
  const [selectedTags, setSelectedTags] = useState<RecipeTag[]>([]);
  const [selectedMealType, setSelectedMealType] = useState<MealType>(MealType[mealSlotSpecification.meal_type]);
  const [selectedMealPreparationTime, setSelectedMealPreparationTime] = useState<SelectedMealPreparationTime>(
    SelectedMealPreparationTime.ALL
  );
  const [selectFavourites, setSelectFavourites] = useState<boolean>(false);
  const [sourceProvider, setSourceProvider] = useState<SourceProvider[]>([SourceProvider.WEEKMEALS]);
  const [ownRecipeModalVisible, setOwnRecipeModalVisible] = useState(false);
  const { showActionSheetWithOptions } = useActionSheet();
  const setRecipeMealSearch = ({ results }: { results: GeneratedRecipeMeal[] }): void => {
    setRecipeMealsByPage({
      byPage: {
        ...recipeMealsByPage.byPage,
        [page]: results,
      },
    });
  };

  // NOTE: This is here to set the state of the search results dirty so we can use useEffect properly
  const [searchCounter, setSearchCounter] = useState<number>(0);
  const resetPage = (): void => {
    setPage(1);
    setSearchCounter(searchCounter + 1);
  };

  const resetRecipeMealSearch = (): void => {
    resetPage();
    setRecipeMealsByPage(recipeMealSearchInitialState);
  };

  let mealGenerateQueryArgs: FoodGenerateMealsListApiArg = {
    mealSlotSpecification: mealSlotSpecification.id,
    ingredientSearch: searchText,
    favourite: selectFavourites,
    sourceProvider,
    tags: selectedTags,
    page,
    user: user.id,
    kcal: mealSlotSpecification.kcal || 0, // NOTE: this is included only for cache busting
    protein: mealSlotSpecification.protein || 0, // NOTE: this is included only for cache busting
  };

  if (selectedMealPreparationTime) {
    mealGenerateQueryArgs = {
      ...mealGenerateQueryArgs,
      ...SELECTED_MEAL_PREPARATION_TIME_TO_QUERY_ARGS[selectedMealPreparationTime],
    };
  }

  if (sourceProvider.includes(SourceProvider.USER_GENERATED) && user) {
    mealGenerateQueryArgs.author = user.id;
  }

  if (selectedMealType !== MealType.ALL) {
    mealGenerateQueryArgs.mealTypes = [selectedMealType];
  }

  const allFoodSearchArgs = [
    selectedTags,
    selectedMealType,
    selectedMealPreparationTime,
    selectFavourites,
    sourceProvider,
  ];

  useEffect(() => {
    resetRecipeMealSearch();
  }, allFoodSearchArgs);

  const {
    data,
    error,
    isLoading: isLoadingFirstTime,
    isFetching,
  } = useFoodGenerateMealsListQuery(mealGenerateQueryArgs);

  // Prefetching
  const onFirstPage = page === 1;
  const moreDataAvailable = Boolean(data?.next);
  const onSubsequentPageButMoreDataAvailable = moreDataAvailable && page > 1;
  const searchingForWeekmealsRecipes = sourceProvider.includes(SourceProvider.WEEKMEALS);
  const searchQueryEmpty = searchText === "";
  const searchingForOriginalMealType = selectedMealType === MealType[mealSlotSpecification.meal_type];
  const searchingForOriginalPrepTime = selectedMealPreparationTime === SelectedMealPreparationTime.ALL;

  const isOriginalQuery =
    !selectFavourites &&
    searchingForWeekmealsRecipes &&
    searchQueryEmpty &&
    searchingForOriginalMealType &&
    searchingForOriginalPrepTime;

  const shouldPrefetchNextPage =
    moreDataAvailable && Boolean((onFirstPage && isOriginalQuery) || onSubsequentPageButMoreDataAvailable);

  const pageToPrefetch = shouldPrefetchNextPage ? page + 1 : page;

  // NOTE: We do the prefetch off the critical path to speed up load times
  useEffect(() => {
    if (!shouldPrefetchNextPage) return;

    const prefetchRequestOptions = {
      ...mealGenerateQueryArgs,
      page: pageToPrefetch,
    };

    store.dispatch(
      // The compiler is wrong here
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      api.util.prefetch(GENERATE_MEALS_ENDPOINT_NAME, prefetchRequestOptions, generateMealsPrefetchOptions)
    );
  }, [shouldPrefetchNextPage]);

  useEffect(() => {
    if (!isFetching && data?.results) {
      setRecipeMealSearch({ results: data.results });
    }
  }, [data, searchCounter]);

  const onPressBack = (): void => {
    navigation.goBack();
  };

  const onSearchTextChange = (value: string): void => {
    resetPage();
    setSearchText(value);
  };

  const onCategoryCardChange = (value: RecipeCategory | RecipeTag): void => {
    logger.debug("onCategoryCard", value);
    resetPage();

    switch (value) {
      case RecipeCategory.FAVOURITE: {
        setSelectFavourites(!selectFavourites);
        // When toggling favourites, reset sourceProvider to search all sources
        // because user favourites can come from any source provider
        if (!selectFavourites) {
          // Turning favourites ON - search all sources
          setSourceProvider([]);
        } else {
          // Turning favourites OFF - go back to default WEEKMEALS
          setSourceProvider([SourceProvider.WEEKMEALS]);
        }
        return;
      }
      case RecipeCategory.OWN_RECIPE: {
        let updatedSourceProvider = [SourceProvider.USER_GENERATED];
        if (sourceProvider.includes(SourceProvider.USER_GENERATED)) {
          updatedSourceProvider = [];
        }

        setSourceProvider(updatedSourceProvider);
        return;
      }
      case RecipeCategory.POPULAR: {
        setSelectedTags([]);
        return;
      }
      default: {
        // This is a RecipeTag
        setSelectedTags([value]);
      }
    }
  };

  const [createCalendarItemFromGeneratedMeal] = usePlannerPlanGeneratedRecipeMealCreateMutation();
  const [createRecipeMealFromRecipeTemplate] = useFoodRecipeMealCreateMutation();

  const [deleteCalendarItemOnBackend] = usePlannerCalendarItemDestroyMutation();

  const [createCalendarItem] = usePlannerCalendarItemCreateMutation();
  const { refetchCalendarDays } = getPlannerData();

  const addRecipeMealToCalendar = async (recipeMeal: RecipeMeal): Promise<void> => {
    await addMealToPlanner({
      currentCalendarDay,
      mealSlot: mealSlotSpecification,
      mealContentType: "recipemeal",
      meal: { ...recipeMeal, content_type: "RecipeMeal", id: recipeMeal.id },
      createCalendarItem,
      refetchCalendarDays,
    });

    refetchCalendarDays();
  };

  const createRecipeMealFromRecipeTemplateWrapper = async (
    recipeTemplate: RecipeTemplate,
    doNotPlan = false
  ): Promise<void> => {
    const recipeTemplateWithImageRemoved = _.omit(recipeTemplate, ["image"]);
    const recipeTemplateRequest: RecipeTemplateRequest = recipeTemplateWithImageRemoved;
    const recipeMealRequest: RecipeMealRequest = {
      recipe_template: recipeTemplateRequest,
      portions: 1,
    };

    const recipeMeal = await createRecipeMealFromRecipeTemplate({
      recipeMealRequest,
    }).unwrap();

    if (!doNotPlan) {
      await addRecipeMealToCalendar(recipeMeal);
    }

    navigation.pop(1);
  };

  const createRecipeMealFromGeneratedMealWrapper = async (
    generatedRecipeMeal: GeneratedRecipeMeal,
    portions: number
  ): Promise<void> => {
    if (!currentCalendarDay.id) {
      throw new Error("currentCalendarDay is not set");
    }

    if (calendarItemToReplace) {
      // NOTE: Since this is used for the "list alternates" feature
      // if there is a calendarItemToReplace, we need to delete it first
      await deleteCalendarItemOnBackend({ id: calendarItemToReplace });
    }

    // NOTE: To speed this up even further this should return the updated CalendarDay on success
    await createCalendarItemFromGeneratedMeal({
      planGeneratedRecipeMealRequest: {
        ...generatedRecipeMeal,
        portions_to_plan: portions,
        // NOTE: we need to remove generatedRecipeMeal.recipe_template.image, note the
        // difference between RecipeTemplateRequest.image (blob) and RecipeTemplate.image (string)
        recipe_template: _.omit(generatedRecipeMeal.recipe_template, "image"),
        calendar_day_id: currentCalendarDay.id,
        meal_slot_specification_id: mealSlotSpecification.id,
      },
    });

    // NOTE: We do 2 here because there is the RecipeSearch screen also in the stack
    navigation.pop(2);
  };

  const onRecipeMealPress = (generatedRecipeMeal: GeneratedRecipeMeal): void => {
    navigation.push(Routes.AddRecipeTemplateStack, {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      screen: Routes.RecipeDetailScreen,
      params: {
        recipeMeal: generatedRecipeMeal,
        mealSlotSpecification,
        mealType: selectedMealType,
        recipeTemplateEditable,
        onSelect: (portions: number) => createRecipeMealFromGeneratedMealWrapper(generatedRecipeMeal, portions),
      },
    });
  };

  const onOpenActionSheet = (): void => {
    const options = ["Edit", "Remove", "Cancel"];
    const editBtnIndex = 0;
    const removeButtonIndex = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: editBtnIndex,
      },
      (buttonIndex) => {
        if (buttonIndex === editBtnIndex) {
          logger.debug("Pressed Edit mode");
        } else if (buttonIndex === removeButtonIndex) {
          logger.debug("Pressed Remove mode");
        }
        // Do something here depending on the button index selected
      }
    );
  };

  const ownMealsFilterSelected =
    sourceProvider?.includes(SourceProvider.USER_GENERATED) && sourceProvider?.length === 1;

  // TODO: These should be in a helpers file when this file is refactored
  const isARecipeTag = (rt: RecipeTagOrCategory): rt is RecipeTag =>
    Object.values(RecipeTag).includes(rt as unknown as RecipeTag);
  const isARecipeCategory = (rc: RecipeTagOrCategory): rc is RecipeCategory =>
    Object.values(RecipeCategory).includes(rc as unknown as RecipeCategory);

  const isPopularSelected = selectedTags === undefined;

  const isCategoryFilterSelected = (category: CategoryFilter): boolean => {
    const isMyCategorySelected = isARecipeTag(category.value) && selectedTags?.includes(category.value);

    return (
      isMyCategorySelected ||
      (selectFavourites ? category.value === RecipeCategory.FAVOURITE : false) ||
      (isPopularSelected && !selectFavourites && !ownMealsFilterSelected
        ? category.value === RecipeCategory.POPULAR
        : false) ||
      (ownMealsFilterSelected ? category.value === RecipeCategory.OWN_RECIPE : false)
    );
  };

  const createCategoryCard = (category: CategoryFilter): JSX.Element => {
    const isCreateRecipeCard = category.value === "CREATE_RECIPE";
    const isSelected = isCreateRecipeCard ? false : isCategoryFilterSelected(category);

    return (
      <Pressable
        key={category.id}
        onPress={() => {
          if (isCreateRecipeCard) {
            navigation.push(Routes.RecipeAddOwnStack, {
              screen: Routes.RecipeAddOwnScreen,
              params: {
                mealSlotSpecification,
                onSave: async (recipeTemplate: RecipeTemplate, doNotPlan?: boolean): Promise<void> => {
                  await createRecipeMealFromRecipeTemplateWrapper(recipeTemplate, doNotPlan);
                  navigation.goBack();
                },
                submitMessage: t("recipe_create.save_as_meal_button_text", {
                  meal: formatMealType(mealSlotSpecification.meal_type, t),
                }),
              },
            });
          } else {
            if (category.value === "CREATE_RECIPE") {
              throw new Error("CREATE_RECIPE should not be a category");
            }

            const selectedCategory = isARecipeCategory(category.value)
              ? RecipeCategory[category.value]
              : RecipeTag[category.value];

            onCategoryCardChange(selectedCategory);
          }
        }}
        _pressed={{ opacity: 0.8 }}
      >
        <Box
          bg={isSelected ? "primary.50" : "white"}
          borderRadius={theme.borderRadii.button.pill}
          px={3}
          py={1.5}
          borderWidth={1}
          borderColor={isSelected ? "primary.500" : "gray.300"}
        >
          <HStack alignItems="center" space={1.5}>
            {category.value === RecipeCategory.FAVOURITE && (
              <Icon
                as={MaterialIcons}
                name={isSelected ? "favorite" : "favorite-outline"}
                size="sm"
                color={isSelected ? "primary.600" : "gray.600"}
              />
            )}
            {category.type === "emoji" && category.emoji ? <Text fontSize="sm">{category.emoji}</Text> : null}
            <Text fontSize="sm" fontWeight="medium" color={isSelected ? "primary.700" : "gray.700"} textAlign="center">
              {category.label}
            </Text>
          </HStack>
        </Box>
      </Pressable>
    );
  };

  const headerComponent = React.useMemo(
    () => (
      <Box bg="white" flex={1}>
        {/* Header Section */}
        <HStack alignItems="center" px={4} py={3} bg="white">
          <IconButton
            onPress={onPressBack}
            icon={<Icon as={MaterialIcons} name="arrow-back" color="gray.700" size="lg" />}
            variant="ghost"
            _pressed={{ bg: "gray.100" }}
            borderRadius="full"
          />
          <HStack alignItems="center" flex={1} ml={2}>
            <Box bg="primary.50" borderRadius="full" p={2} mr={3}>
              <Icon as={MaterialIcons} name="restaurant" color="white" size="sm" />
            </Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              {formatMealType(mealSlotSpecification.meal_type, t)}
            </Text>
          </HStack>
        </HStack>

        <VStack flex={1} bg="white" px={4} py={3} space={3}>
          {/* Search Input */}
          <Box>
            <Input
              placeholder={t("recipe_search.search_prompt")}
              onChangeText={onSearchTextChange}
              value={searchText}
              InputLeftElement={<Icon as={MaterialIcons} mx="2" size="md" name="search" color="primary.600" />}
              testID="recipe-search-input"
              borderRadius={theme.borderRadii.input.default}
              bg="white"
              borderColor="gray.300"
              borderWidth={1}
              height={12}
              fontSize="sm"
              _focus={{
                borderColor: "primary.500",
                bg: "white",
              }}
            />
          </Box>

          {/* Filters Row - Favorite button and dropdowns on same line */}
          <HStack space={2} alignItems="center">
            {/* Favorite Button */}
            <Pressable
              onPress={() => {
                onCategoryCardChange(RecipeCategory.FAVOURITE);
              }}
              _pressed={{ opacity: 0.8 }}
            >
              <Box
                bg={selectFavourites ? "primary.50" : "white"}
                borderRadius={theme.borderRadii.button.default}
                px={3}
                py={2}
                borderWidth={1}
                borderColor={selectFavourites ? "primary.500" : "gray.300"}
              >
                <HStack alignItems="center" space={1.5}>
                  <Icon
                    as={MaterialIcons}
                    name={selectFavourites ? "favorite" : "favorite-outline"}
                    size="sm"
                    color={selectFavourites ? "primary.600" : "gray.600"}
                  />
                  <Text fontSize="sm" fontWeight="medium" color={selectFavourites ? "primary.700" : "gray.700"}>
                    {t("recipe_search.category_favourite")}
                  </Text>
                </HStack>
              </Box>
            </Pressable>

            {/* Meal Type Dropdown */}
            <Box flex={1}>
              <Select
                selectedValue={selectedMealType}
                placeholder={t("recipe_search.meal_type_filter_text")}
                onValueChange={(itemValue) => setSelectedMealType(MealType[itemValue as MealTypeEnum])}
                testID="meal-type-select"
                bg="white"
                borderColor="gray.300"
                borderRadius={theme.borderRadii.button.default}
                borderWidth={1}
                fontSize="sm"
                color="gray.700"
                placeholderTextColor="gray.500"
                height={10}
                py={2}
                dropdownIcon={<ChevronDownIcon size="4" color="primary.600" mr={2} />}
                _selectedItem={{
                  bg: "primary.50",
                  endIcon: <CheckIcon size="4" color="primary.600" />,
                }}
                _text={{
                  fontSize: "sm",
                  color: "gray.700",
                }}
              >
                {MEAL_TYPE_LIST.map((option, index) => (
                  <Select.Item key={index} label={option.label} value={option.value} />
                ))}
              </Select>
            </Box>

            {/* Preparation Time Dropdown */}
            <Box flex={1}>
              <Select
                selectedValue={selectedMealPreparationTime || ""}
                placeholder={t("recipe_search.preparation_time_filter_text")}
                onValueChange={(itemValue) => {
                  setSelectedMealPreparationTime(itemValue as SelectedMealPreparationTime);
                }}
                testID="preparationTime-select"
                bg="white"
                borderColor="gray.300"
                borderRadius={theme.borderRadii.button.default}
                borderWidth={1}
                fontSize="sm"
                color="gray.700"
                placeholderTextColor="gray.500"
                height={10}
                py={2}
                dropdownIcon={<ChevronDownIcon size="4" color="primary.600" mr={2} />}
                _selectedItem={{
                  bg: "primary.50",
                  endIcon: <CheckIcon size="4" color="primary.600" />,
                }}
                _text={{
                  fontSize: "sm",
                  color: "gray.700",
                }}
              >
                {(Object.keys(SelectedMealPreparationTime) as Array<keyof typeof SelectedMealPreparationTime>).map(
                  (value, index) => (
                    <Select.Item
                      key={index}
                      label={t(`recipe_search.preparation_time_filter_options.${value}`)}
                      value={value}
                    />
                  )
                )}
              </Select>
            </Box>
          </HStack>

          {/* Other Filter Pills (Create Recipe, Own Recipe) */}
          {FILTERS_TO_SHOW.filter((f) => f.value !== RecipeCategory.FAVOURITE).length > 0 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ paddingVertical: 2 }}
            >
              <HStack space={2}>
                {FILTERS_TO_SHOW.filter((f) => f.value !== RecipeCategory.FAVOURITE).map(createCategoryCard)}
              </HStack>
            </ScrollView>
          )}
        </VStack>
      </Box>
    ),
    [
      selectedMealType,
      selectedMealPreparationTime,
      selectedTags,
      selectFavourites,
      sourceProvider,
      ownMealsFilterSelected,
      t,
      onPressBack,
      onSearchTextChange,
      mealSlotSpecification,
      MEAL_TYPE_LIST,
      FILTERS_TO_SHOW,
      navigation,
      createRecipeMealFromRecipeTemplateWrapper,
    ]
  );

  const searchResultsList = _.map(Object.values(recipeMealsByPage.byPage).flat(), (recipeMeal) => ({
    ...recipeMeal,
    key: recipeMeal.recipe_template.id,
  }));

  const loadingAnotherPage = !isLoadingFirstTime && isFetching && page > 1;

  /**
   * This safely parses the next page value and then sets the next page.
   *
   * Note: If we just did setPage(page + 1) then we could get into a loop
   * due to the scroll constantly triggering `onEndReachedThreshold` - this logic prevents that
   *
   */
  const onEndReachedFunc = (): void => {
    if (data?.next) {
      const parsedNextUrl = URI.parse(data.next);

      if (parsedNextUrl.query) {
        const parsedUrlQuery = queryString.parse(parsedNextUrl.query);

        if (parsedUrlQuery.page && typeof parsedUrlQuery.page === "string") {
          const pageAsNumber = parseInt(parsedUrlQuery.page, 10);

          const pageAsNumberIsValid = Number.isInteger(pageAsNumber) && pageAsNumber > 1;
          if (pageAsNumberIsValid) {
            setPage(pageAsNumber);
            return;
          }
        }
      }

      throw new Error(`Failed to parse next url:${data.next}`);
    }
  };

  const renderItemFunction = ({
    item: generatedRecipeMeal,
    index,
  }: {
    item: GeneratedRecipeMeal;
    index: number;
  }): JSX.Element => (
    <MemoizedRecipeMealCard
      recipeMeal={generatedRecipeMeal}
      onButtonPress={() => onRecipeMealPress(generatedRecipeMeal)}
      onOpenAction={onOpenActionSheet}
      mealSlotSpecification={mealSlotSpecification}
      index={index}
    />
  );

  // Responsive column logic: 4 columns on desktop, 2 on mobile
  const numColumns = isDesktopScreen() ? 4 : 2;

  return (
    <Box flex={1} bg="white" safeAreaTop nativeID="recipeSearchScreen">
      <FlatList
        ListHeaderComponent={headerComponent}
        ListFooterComponent={
          loadingAnotherPage ? (
            <Center py={4}>
              <Spinner color="primary.600" size="lg" />
            </Center>
          ) : null
        }
        data={searchResultsList}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}
        onEndReached={onEndReachedFunc}
        onEndReachedThreshold={0.2}
        bounces
        renderItem={renderItemFunction}
        keyExtractor={(item) => String(item.recipe_template.id)}
        showsVerticalScrollIndicator={false}
        numColumns={numColumns}
        columnWrapperStyle={numColumns > 1 ? { paddingHorizontal: 16 } : undefined}
        ListEmptyComponent={() => (
          <Center flex={1} px={6} py={12} minH="300">
            {isFetching || isLoadingFirstTime ? (
              <VStack alignItems="center" space={4}>
                <Spinner color="primary.600" size="lg" />
                <Text fontSize="md" color="gray.500" textAlign="center" fontWeight="medium">
                  Searching for recipes...
                </Text>
              </VStack>
            ) : (
              <VStack alignItems="center" space={4} maxW="280">
                <Text fontSize="3xl" textAlign="center" mb={2}>
                  🔍
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color="gray.700" textAlign="center">
                  No recipes found
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center" lineHeight="sm">
                  Try adjusting your search terms or filters to find more recipes
                </Text>
              </VStack>
            )}
          </Center>
        )}
        keyboardShouldPersistTaps="handled"
        testID="recipeResults-list"
        nativeID="recipeResults-list"
      />
    </Box>
  );
};
export default RecipeSearchScreen;
